import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { fetchJobs } from "./api";

interface Job {
  id: string;
  started_at: string;
  finished_at: string;
  n_trials: number;
  n_errors: number;
  metrics: Record<string, any>;
  modified: string;
}

/**
 * Format duration in milliseconds to a human-readable string.
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(1)}s`;
  } else if (ms < 3600000) {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
}

/**
 * Format date to a more readable format.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  return date.toLocaleString('en-US', options);
}

export function JobList() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs()
      .then(data => {
        setJobs(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to load jobs:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="text-center py-8">Loading jobs...</div>;
  }

  return (
    <div className="space-y-2">
      <h2 className="text-2xl font-bold mb-4">Benchmark Jobs</h2>
      {jobs.length === 0 ? (
        <div className="text-center py-8 text-gray-600">No jobs found</div>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => {
            const successRate = job.n_trials > 0 
              ? Object.values(job.metrics).reduce((acc: number, metric: any) => {
                  const rewards = metric.reward_stats?.reward || {};
                  const success = rewards["1.0"]?.length || 0;
                  const total = Object.values(rewards).flat().length;
                  return acc + (total > 0 ? success / total : 0);
                }, 0) / Object.keys(job.metrics).length
              : 0;
            
            // Calculate total duration
            const startedAt = new Date(job.started_at).getTime();
            const finishedAt = new Date(job.finished_at).getTime();
            const duration = finishedAt - startedAt;
            
            return (
              <div
                key={job.id}
                onClick={() => navigate({ to: "/jobs/$jobId", params: { jobId: job.id } })}
                className="p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{job.id}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatDate(job.started_at)}
                    </p>
                    {duration > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Duration: <span className="text-gray-700 font-medium">{formatDuration(duration)}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm">
                      <span className={successRate === 1 ? "text-green-600" : successRate > 0 ? "text-yellow-600" : "text-gray-600"}>
                        {Math.round(successRate * 100)}%
                      </span>
                      <span className="text-gray-600"> success</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {job.n_trials} trials
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
